"use client";
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addSale, createDraft, updateDraft, fetchSaleDetails } from "@/state/sales/sale.slice";
import { getProducts } from "@/state/products/product.slice";
import { fetchBusiness } from "@/state/business/business.slice";
import { useToast } from "@/components/ui/use-toast";
import ProductSearch from "@/components/custom/sales/ProductSearch";
import SaleItems from "@/components/custom/sales/SaleItems";
import SaleSummary from "@/components/custom/sales/SaleSummary";
import Receipt from "@/components/custom/sales/Receipt";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, Check, Search, Plus, User, Calendar, Save, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { useReactToPrint } from "react-to-print";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/ui/command";

export default function CreateSalePage() {
    const dispatch = useDispatch();
    const { toast } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const draftId = searchParams.get('draft');

    const { products, isLoading: loadingProducts } = useSelector((state) => state.product);
    const { loading: loadingSale, currentSale } = useSelector((state) => state.sales);
    const { business } = useSelector((state) => state.business || {});
    const { user } = useSelector((state) => state.auth);

    // Draft editing state
    const [editingDraftId, setEditingDraftId] = useState(null);
    const [draftLoaded, setDraftLoaded] = useState(false);

    const [saleItems, setSaleItems] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [saleType, setSaleType] = useState("retail");
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [paidAmount, setPaidAmount] = useState("");
    const [dueDate, setDueDate] = useState(""); // For credit sales repayment scheduling
    const [saleDate, setSaleDate] = useState(() => {
        const now = new Date();
        const offset = now.getTimezoneOffset() * 60000;
        return new Date(now - offset).toISOString().slice(0, 19);
    }); // Sale date with today as default (local time with seconds)
    const [lastSale, setLastSale] = useState(null);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [isUnpaidSale, setIsUnpaidSale] = useState(false);

    // Customer search and add states
    const [customerSearch, setCustomerSearch] = useState("");
    const [customerPopoverOpen, setCustomerPopoverOpen] = useState(false);
    const [showAddCustomerDialog, setShowAddCustomerDialog] = useState(false);
    const [newCustomer, setNewCustomer] = useState({ name: "", phone: "", email: "" });
    const [addingCustomer, setAddingCustomer] = useState(false);

    // Print Logic - main receipt (paid)
    const receiptRef = useRef();
    const handlePrint = useReactToPrint({
        contentRef: receiptRef,
        pageStyle: `
            @page { size: auto; margin: 10mm; }
            @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
        `
    });

    // Preview receipt for current cart (before completing sale)
    const previewReceiptRef = useRef();
    const handlePreviewPrint = useReactToPrint({
        contentRef: previewReceiptRef,
        pageStyle: `
            @page { size: auto; margin: 10mm; }
            @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
        `
    });

    useEffect(() => {
        // Fetch all products (high limit) for POS search - bypasses default pagination
        dispatch(getProducts({ limit: 10000 }));
        dispatch(fetchBusiness());
        fetchCustomers();

        // Load draft if editing
        if (draftId && !draftLoaded) {
            dispatch(fetchSaleDetails(draftId));
            setEditingDraftId(draftId);
        }
    }, [dispatch, draftId, draftLoaded]);

    // Effect to populate form from loaded draft
    useEffect(() => {
        if (currentSale && editingDraftId && !draftLoaded && currentSale.id?.toString() === editingDraftId) {
            // Populate form from draft
            setSaleType(currentSale.type || 'retail');
            setSelectedCustomer(currentSale.customer_id?.toString() || null);
            setPaymentMethod(currentSale.method || 'Cash');
            setPaidAmount(currentSale.amount_paid > 0 ? currentSale.amount_paid.toString() : '');
            setDueDate(currentSale.due_date ? new Date(currentSale.due_date).toISOString().split('T')[0] : '');
            if (currentSale.createdAt) {
                const date = new Date(currentSale.createdAt);
                const offset = date.getTimezoneOffset() * 60000;
                setSaleDate(new Date(date - offset).toISOString().slice(0, 19));
            } else {
                const now = new Date();
                const offset = now.getTimezoneOffset() * 60000;
                setSaleDate(new Date(now - offset).toISOString().slice(0, 19));
            }

            // Convert SaleItems to local cart format
            if (currentSale.SaleItems && currentSale.SaleItems.length > 0) {
                const cartItems = currentSale.SaleItems.map(item => {
                    const product = products.find(p => p.id === item.product_id);
                    return {
                        product: product || { id: item.product_id, title: item.title, name: item.title },
                        quantity: item.qty,
                        price: parseFloat(item.product_price)
                    };
                }).filter(item => item.product);
                setSaleItems(cartItems);
            }
            setDraftLoaded(true);
            toast({ title: "Draft Loaded", description: "You are editing a saved draft" });
        }
    }, [currentSale, editingDraftId, draftLoaded, products, toast]);

    const fetchCustomers = async () => {
        try {
            const res = await axios.get(`${import.meta.env.VITE_API_URL}customers`, { withCredentials: true });
            if (res.data.success) {
                setCustomers(res.data.data);
            }
        } catch (e) { console.error("Failed to fetch customers"); }
    }

    // Add new customer
    const handleAddCustomer = async () => {
        if (!newCustomer.name.trim()) {
            toast({ variant: "destructive", title: "Error", description: "Customer name is required" });
            return;
        }
        setAddingCustomer(true);
        try {
            const res = await axios.post(`${import.meta.env.VITE_API_URL}customers`, newCustomer, { withCredentials: true });
            if (res.data.success) {
                const addedCustomer = res.data.data;
                setCustomers(prev => [...prev, addedCustomer]);
                setSelectedCustomer(addedCustomer.id.toString());
                setShowAddCustomerDialog(false);
                setNewCustomer({ name: "", phone: "", email: "" });
                toast({ title: "Success", description: "Customer added successfully" });
            }
        } catch (e) {
            toast({ variant: "destructive", title: "Error", description: e.response?.data?.message || "Failed to add customer" });
        } finally {
            setAddingCustomer(false);
        }
    };

    // Filter customers by search
    const filteredCustomers = customers.filter(c =>
        c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
        c.phone?.includes(customerSearch)
    );

    // Get selected customer object
    const selectedCustomerObj = customers.find(c => c.id?.toString() === selectedCustomer);

    const handleProductSelect = (product) => {
        if (!product?.id) return;

        // Check if user has permission to sell from this product's shop
        const productShopId = product.shop_id;
        const userShopId = user?.shop_id;
        const shopSellPermissions = user?.shop_sell_permissions || [];

        // Allow if:
        // 1. Product has no shop assigned (shop_id is null)
        // 2. Product is from user's assigned shop
        // 3. User has sell permission for this product's shop
        const canSellProduct =
            productShopId === null ||
            productShopId === userShopId ||
            shopSellPermissions.includes(productShopId);

        if (!canSellProduct) {
            toast({
                variant: "destructive",
                title: "Permission Denied",
                description: "You don't have permission to sell products from this shop"
            });
            return;
        }

        const existingItem = saleItems.find((item) => item?.product?.id === product?.id);

        if (existingItem) {
            handleUpdateQuantity(product?.id, (existingItem?.quantity || 0) + 1);
        } else {
            setSaleItems([
                ...saleItems,
                {
                    product,
                    quantity: 1,
                    price: saleType === 'wholesale' ? parseFloat(product.wholesale_price) : parseFloat(product.current_price),
                },
            ]);
        }
    };

    const handleUpdateQuantity = (productId, newQuantity) => {
        if (!productId || newQuantity < 1) return;
        // Check stock logic could go here if we had local stock data
        setSaleItems(
            saleItems.map((item) =>
                item?.product?.id === productId
                    ? { ...item, quantity: newQuantity }
                    : item
            )
        );
    };

    const handleRemoveItem = (productId) => {
        setSaleItems(saleItems.filter((item) => item?.product?.id !== productId));
    };

    const handleUpdatePrice = (productId, newPrice) => {
        if (!productId) return;
        setSaleItems(
            saleItems.map((item) => {
                if (item?.product?.id === productId) {
                    const minPrice = parseFloat(item.product.current_price || 0);
                    // Ensure price is not below retail price
                    const validPrice = Math.max(newPrice, minPrice);
                    return { ...item, price: validPrice };
                }
                return item;
            })
        );
    };

    const handleUpdateImei = (productId, imei) => {
        if (!productId) return;
        setSaleItems(
            saleItems.map((item) =>
                item?.product?.id === productId
                    ? { ...item, imei: imei }
                    : item
            )
        );
    };

    // Recalculate prices when sale type changes
    useEffect(() => {
        setSaleItems(prevItems => prevItems.map(item => ({
            ...item,
            price: saleType === 'wholesale' ? parseFloat(item.product.wholesale_price) : parseFloat(item.product.current_price)
        })));
    }, [saleType]);

    const subtotal = saleItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const taxRate = parseFloat(business?.tax_rate) || 0;
    const tax = (subtotal * taxRate) / 100;
    const total = subtotal + tax;

    const handleSave = async () => {
        if (saleItems.length === 0) {
            toast({ variant: "destructive", title: "Error", description: "Cart is empty" });
            return;
        }

        // Validate stock locally if possible (we have product.Stocks usually but might be complex with batches)
        // Rely on server error for now.

        const saleData = {
            items: saleItems.map(item => ({
                product_id: item.product.id,
                quantity: item.quantity,
                custom_price: item.price, // Send custom/edited price
                imei: item.imei || null, // Optional IMEI/Serial number
            })),
            customerId: selectedCustomer,
            type: saleType,
            paymentMethod: paymentMethod === "Credit" ? "Credit" : paymentMethod,
            // For Credit method, paid amount is 0. For others, use entered amount or full total
            paidAmount: paymentMethod === "Credit" ? 0 : (paidAmount ? parseFloat(paidAmount) : total),
            tax,
            discount: 0,
            due_date: dueDate || null, // For credit sales repayment scheduling
            sale_date: saleDate, // Include sale date
        };

        try {
            const result = await dispatch(addSale(saleData)).unwrap();
            // Build complete sale object with items for receipt printing
            // (API may not return SaleItems, so we use local cart data)
            if (result) {
                const completeSale = {
                    ...result,
                    method: paymentMethod, // Include payment method
                    SaleItems: saleItems.map(item => ({
                        qty: item.quantity,
                        title: item.product.title || item.product.name, // Use title with name fallback
                        product_price: item.price,
                        total: item.price * item.quantity
                    })),
                    Customer: selectedCustomer ? customers.find(c => c.id.toString() === selectedCustomer) : null,
                    total: total,
                    tax: tax,
                    discount: 0
                };
                setLastSale(completeSale);
                // Check if it's unpaid or partial
                const paid = paidAmount ? parseFloat(paidAmount) : total;
                setIsUnpaidSale(paid < total);
                // Show success dialog
                setSuccessDialogOpen(true);
            }
            setSaleItems([]);
            setPaidAmount("");
            setSelectedCustomer(null);
            setEditingDraftId(null); // Clear draft editing mode
            setDraftLoaded(false);
        } catch (err) {
            toast({ variant: "destructive", title: "Error", description: err });
        }
    };

    // Save as Draft
    const handleSaveAsDraft = async () => {
        if (saleItems.length === 0) {
            toast({ variant: "destructive", title: "Error", description: "Cart is empty" });
            return;
        }

        const draftData = {
            items: saleItems.map(item => ({
                product_id: item.product.id,
                quantity: item.quantity,
                custom_price: item.price,
            })),
            customerId: selectedCustomer,
            type: saleType,
            paymentMethod: paymentMethod === "Credit" ? "Credit" : paymentMethod,
            paidAmount: paymentMethod === "Credit" ? 0 : (paidAmount ? parseFloat(paidAmount) : 0),
            tax,
            discount: 0,
            due_date: dueDate || null,
            sale_date: saleDate,
        };

        try {
            if (editingDraftId) {
                // Update existing draft
                await dispatch(updateDraft({ id: editingDraftId, data: draftData })).unwrap();
                toast({ title: "Success", description: "Draft updated successfully" });
            } else {
                // Create new draft
                await dispatch(createDraft(draftData)).unwrap();
                toast({ title: "Success", description: "Draft saved successfully" });
            }
            // Clear form
            setSaleItems([]);
            setPaidAmount("");
            setSelectedCustomer(null);
            setEditingDraftId(null);
            setDraftLoaded(false);
        } catch (err) {
            toast({ variant: "destructive", title: "Error", description: err });
        }
    };

    // Print receipt for preview (before sale, with UNPAID watermark)
    const onPrintPreview = () => {
        if (saleItems.length === 0) {
            toast({ variant: "destructive", title: "Error", description: "Cart is empty" });
            return;
        }
        handlePreviewPrint();
    };

    // Print final receipt after sale
    const onPrintReceipt = () => {
        if (lastSale) {
            handlePrint();
        }
        setSuccessDialogOpen(false);
    };

    // Close dialog without printing
    const onCloseDialog = () => {
        setSuccessDialogOpen(false);
        toast({ title: "Success", description: "Sale completed!" });
    };

    // Generate receipt text for sharing
    const generateReceiptText = (sale) => {
        const items = sale.SaleItems || [];
        const itemsText = items.map(item =>
            `• ${item.title || item.name || 'Item'} x${item.qty} - ${businessDetails?.currency || 'GHS'} ${parseFloat(item.total).toFixed(2)}`
        ).join('\n');

        const receiptText = `
🧾 *${businessDetails?.name || 'Receipt'}*
━━━━━━━━━━━━━━━━━━
📋 Invoice: ${sale.order_number || 'NEW'}
📅 Date: ${sale.createdAt ? new Date(sale.createdAt).toLocaleDateString() : new Date().toLocaleDateString()}
👤 Customer: ${sale.Customer?.name || 'Walk-in'}

*Items:*
${itemsText}

━━━━━━━━━━━━━━━━━━
💰 *Total: ${businessDetails?.currency || 'GHS'} ${parseFloat(sale.total).toFixed(2)}*

Thank you for your business! 🙏
`.trim();

        return receiptText;
    };

    const handleShareWhatsApp = () => {
        if (!lastSale) return;
        const receiptText = generateReceiptText(lastSale);
        const encodedText = encodeURIComponent(receiptText);
        const customerPhone = lastSale.Customer?.phone?.replace(/[^0-9]/g, '');
        if (customerPhone) {
            window.open(`https://wa.me/${customerPhone}?text=${encodedText}`, '_blank');
        } else {
            window.open(`https://wa.me/?text=${encodedText}`, '_blank');
        }
        setSuccessDialogOpen(false);
    };

    const handleShareTelegram = () => {
        if (!lastSale) return;
        const receiptText = generateReceiptText(lastSale);
        const encodedText = encodeURIComponent(receiptText);
        window.open(`https://t.me/share/url?url=&text=${encodedText}`, '_blank');
        setSuccessDialogOpen(false);
    };

    const businessDetails = business || null;

    // Build preview sale object from cart for preview printing
    const previewSale = {
        order_number: 'PREVIEW',
        createdAt: new Date().toISOString(),
        total: total,
        tax: tax,
        discount: 0,
        method: paymentMethod, // Include payment method
        SaleItems: saleItems.map(item => ({
            qty: item.quantity,
            title: item.product.title || item.product.name, // Use title with name fallback
            product_price: item.price,
            total: item.price * item.quantity
        })),
        Customer: selectedCustomer ? customers.find(c => c.id.toString() === selectedCustomer) : null
    };

    return (
        <div className="mx-auto p-2 sm:p-4 space-y-4 sm:space-y-6 h-[calc(100vh-60px)] sm:h-[calc(100vh-80px)] overflow-y-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-4">
                    <Link to="/dashboard/sales">
                        <Button variant="outline" size="icon"><ArrowLeft className="h-4 w-4" /></Button>
                    </Link>
                    <h1 className="text-2xl font-bold">New Sale</h1>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-white dark:bg-slate-900 justify-between p-3 border rounded shadow-sm">
                    <div className="flex items-center gap-2 justify-between sm:justify-start w-full sm:w-auto">
                        <Label>Type:</Label>
                        <RadioGroup defaultValue="retail" value={saleType} onValueChange={setSaleType} className="flex gap-4">
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="retail" id="r1" />
                                <Label htmlFor="r1">Retail</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="wholesale" id="r2" />
                                <Label htmlFor="r2">Wholesale</Label>
                            </div>
                        </RadioGroup>
                    </div>

                    <div className="hidden sm:block w-[1px] h-8 bg-gray-200 mx-0"></div>

                    {/* Customer Search/Select with Add New */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                        <Label className="sm:hidden text-xs text-muted-foreground uppercase font-semibold tracking-wider">Customer</Label>
                        <Popover open={customerPopoverOpen} onOpenChange={setCustomerPopoverOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={customerPopoverOpen}
                                    className="w-full sm:w-[220px] justify-between"
                                >
                                    <User className="mr-2 h-4 w-4 text-muted-foreground" />
                                    {selectedCustomerObj ? selectedCustomerObj.name : "Walk-in Customer"}
                                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[280px] p-0" align="start">
                                <Command>
                                    <CommandInput
                                        placeholder="Search customer..."
                                        value={customerSearch}
                                        onValueChange={setCustomerSearch}
                                    />
                                    <CommandList className="max-h-[200px]">
                                        <CommandEmpty>
                                            <div className="py-2 text-center text-sm text-muted-foreground">
                                                No customer found.
                                            </div>
                                        </CommandEmpty>
                                        <CommandGroup heading="Customers">
                                            <CommandItem
                                                onSelect={() => {
                                                    setSelectedCustomer(null);
                                                    setCustomerPopoverOpen(false);
                                                    setCustomerSearch("");
                                                }}
                                                className="cursor-pointer"
                                            >
                                                <User className="mr-2 h-4 w-4" />
                                                Walk-in Customer
                                                {!selectedCustomer && <Check className="ml-auto h-4 w-4" />}
                                            </CommandItem>
                                            {filteredCustomers.map((c) => (
                                                <CommandItem
                                                    key={c.id}
                                                    onSelect={() => {
                                                        setSelectedCustomer(c.id.toString());
                                                        setCustomerPopoverOpen(false);
                                                        setCustomerSearch("");
                                                    }}
                                                    className="cursor-pointer"
                                                >
                                                    <div className="flex flex-col">
                                                        <span>{c.name}</span>
                                                        {c.phone && <span className="text-xs text-muted-foreground">{c.phone}</span>}
                                                    </div>
                                                    {selectedCustomer === c.id.toString() && <Check className="ml-auto h-4 w-4" />}
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </CommandList>
                                    <div className="border-t p-1">
                                        <Button
                                            variant="ghost"
                                            className="w-full justify-start text-primary"
                                            onClick={() => {
                                                setCustomerPopoverOpen(false);
                                                setShowAddCustomerDialog(true);
                                                setNewCustomer({ name: customerSearch, phone: "", email: "" });
                                            }}
                                        >
                                            <Plus className="mr-2 h-4 w-4" />
                                            Add New Customer
                                        </Button>
                                    </div>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>

                    <div className="hidden sm:block w-[1px] h-8 bg-gray-200 mx-0"></div>

                    {/* Sale Date */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                        <Label className="sm:hidden text-xs text-muted-foreground uppercase font-semibold tracking-wider">Date</Label>
                        <div className="relative w-full sm:w-auto">
                            {/* <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /> */}
                            <Input
                                type="datetime-local"
                                step="1"
                                className="w-full sm:w-[240px]"
                                value={saleDate}
                                onChange={(e) => setSaleDate(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid gap-4 sm:gap-6 md:grid-cols-3 min-h-[min-content] md:h-full pb-20">
                <div className="md:col-span-2 space-y-4 flex flex-col md:h-full">
                    <div className="bg-white dark:bg-slate-900 p-2 sm:p-4 border rounded shadow-sm flex-shrink-0">
                        <ProductSearch products={products} onSelect={handleProductSelect} />
                    </div>
                    <div className="flex-grow overflow-auto min-h-[300px] md:min-h-0 bg-white dark:bg-slate-900 border rounded shadow-sm">
                        <SaleItems
                            items={saleItems}
                            onUpdateQuantity={handleUpdateQuantity}
                            onRemoveItem={handleRemoveItem}
                            onUpdatePrice={handleUpdatePrice}
                            onUpdateImei={handleUpdateImei}
                        />
                    </div>
                </div>
                <div>
                    {/* Payment & Summary */}
                    <div className="bg-white dark:bg-slate-900 p-4 border shadow-sm space-y-4">
                        <h3 className="font-semibold text-lg">Payment Details</h3>

                        <div className="space-y-2">
                            <Label>Payment Method</Label>
                            <Select value={paymentMethod} onValueChange={(value) => {
                                // Prevent Credit for walk-in customers
                                if (value === "Credit" && (!selectedCustomer || selectedCustomer === "null")) {
                                    toast({ variant: "destructive", title: "Credit Not Allowed", description: "Credit sales require a registered customer. Please select a customer." });
                                    return;
                                }
                                setPaymentMethod(value);
                                // Auto-set due date to today when Credit is selected
                                if (value === "Credit" && !dueDate) {
                                    setDueDate(new Date().toISOString().split('T')[0]);
                                }
                                // Clear paid amount when Credit is selected
                                if (value === "Credit") {
                                    setPaidAmount("");
                                }
                            }}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Cash">Cash</SelectItem>
                                    <SelectItem value="Momo">Mobile Money</SelectItem>
                                    <SelectItem value="Card">Card</SelectItem>
                                    <SelectItem value="Check">Check</SelectItem>
                                    <SelectItem value="Credit" disabled={!selectedCustomer || selectedCustomer === "null"}>
                                        Credit (Pay Later) {(!selectedCustomer || selectedCustomer === "null") && "- Select Customer"}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            {(!selectedCustomer || selectedCustomer === "null") && (
                                <p className="text-xs text-amber-600">💡 To sell on credit, select a registered customer above</p>
                            )}
                        </div>

                        {/* Amount Paid - Hide when Credit is selected */}
                        {paymentMethod !== "Credit" && (
                            <div className="space-y-2">
                                <Label>Amount Paid (GHS)</Label>
                                <Input
                                    type="number"
                                    placeholder="Enter amount..."
                                    value={paidAmount}
                                    onChange={(e) => setPaidAmount(e.target.value)}
                                />
                                {paidAmount === "" ? (
                                    <p className="text-xs text-muted-foreground text-right w-full">Leave empty for full payment (GHS {total.toFixed(2)})</p>
                                ) : !isNaN(parseFloat(paidAmount)) && (
                                    <div className={`flex justify-between items-center p-2 rounded-md mt-2 border ${parseFloat(paidAmount) >= total ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
                                        <span className={`text-sm font-medium ${parseFloat(paidAmount) >= total ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                                            {parseFloat(paidAmount) >= total ? "Customer Change:" : "Balance Due:"}
                                        </span>
                                        <span className={`text-lg font-bold tracking-tight ${parseFloat(paidAmount) >= total ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                                            GHS {Math.abs(parseFloat(paidAmount) - total).toFixed(2)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Due Date - Show for credit sales (Credit method OR partial payment) */}
                        {(paymentMethod === "Credit" || (paidAmount !== "" && parseFloat(paidAmount) < total)) && (
                            <div className="space-y-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                                <Label className="text-yellow-800 dark:text-yellow-200">Credit Sale - Due Date</Label>
                                <Input
                                    type="date"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                    min={new Date().toISOString().split('T')[0]}
                                    className="bg-white dark:bg-slate-800"
                                />
                                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                    {paymentMethod === "Credit"
                                        ? "Customer will pay the full amount later on this date"
                                        : "Set a repayment date for the remaining balance"}
                                </p>
                            </div>
                        )}

                        <div className="border-t pt-4 mt-4">
                            <SaleSummary
                                subtotal={subtotal}
                                tax={tax}
                                taxRate={taxRate}
                                total={total}
                                onSave={handleSave}
                                onPrint={onPrintPreview}
                                isLoading={loadingSale}
                                hidePaymentMethod={true} // Custom handling above
                            />

                            {/* Save as Draft Button */}
                            <Button
                                variant="outline"
                                className="w-full mt-3 gap-2"
                                onClick={handleSaveAsDraft}
                                disabled={loadingSale || saleItems.length === 0}
                            >
                                <Save className="h-4 w-4" />
                                {editingDraftId ? "Update Draft" : "Save as Draft"}
                            </Button>

                            {editingDraftId && (
                                <p className="text-xs text-center text-indigo-600 mt-2">
                                    Editing Draft #{editingDraftId}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Success Dialog - Ask to print or not */}
            <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Check className="h-5 w-5 text-green-600" />
                            Sale Completed!
                        </DialogTitle>
                        <DialogDescription>
                            {isUnpaidSale
                                ? "Sale created with partial payment. Would you like to print a receipt?"
                                : "Sale completed successfully. Would you like to print a receipt?"
                            }
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <div className="flex flex-col gap-2 w-full border-t pt-6">

                            <div className="flex gap-2 w-full">
                                <Button variant="outline" onClick={onCloseDialog} className="flex-1">
                                    No Receipt
                                </Button>
                                <Button onClick={onPrintReceipt} className="flex-1 gap-2">
                                    <Printer className="h-4 w-4" />
                                    Print
                                </Button>
                            </div>
                            <div className="flex gap-2 w-full">
                                <Button variant="outline" size="sm" onClick={handleShareWhatsApp} className="flex-1 text-green-600 border-green-200 hover:bg-green-50">
                                    <Share2 className="mr-2 h-4 w-4" /> WhatsApp
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleShareTelegram} className="flex-1 text-blue-500 border-blue-200 hover:bg-blue-50">
                                    <Share2 className="mr-2 h-4 w-4" /> Telegram
                                </Button>
                            </div>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog >

            {/* Add Customer Dialog */}
            <Dialog open={showAddCustomerDialog} onOpenChange={setShowAddCustomerDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <User className="h-5 w-5" />
                            Add New Customer
                        </DialogTitle>
                        <DialogDescription>
                            Create a new customer to use for this sale.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="customer-name">Name *</Label>
                            <Input
                                id="customer-name"
                                placeholder="Customer name"
                                value={newCustomer.name}
                                onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="customer-phone">Phone</Label>
                            <Input
                                id="customer-phone"
                                placeholder="Phone number"
                                value={newCustomer.phone}
                                onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="customer-email">Email</Label>
                            <Input
                                id="customer-email"
                                type="email"
                                placeholder="Email address"
                                value={newCustomer.email}
                                onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setShowAddCustomerDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleAddCustomer} disabled={addingCustomer}>
                            {addingCustomer ? "Adding..." : "Add Customer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Hidden Receipt Components for Printing */}
            {/* Preview Receipt - for printing before sale (UNPAID watermark) */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                <Receipt
                    ref={previewReceiptRef}
                    sale={previewSale}
                    businessDetails={businessDetails}
                    paymentStatus="draft"
                />
            </div>

            {/* Final Receipt - for printing after sale */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                <Receipt
                    ref={receiptRef}
                    sale={lastSale}
                    businessDetails={businessDetails}
                    paymentStatus={lastSale?.payment_status || (isUnpaidSale ? 'unpaid' : 'paid')}
                />
            </div>
        </div >
    );
}
